import { ArrowRightIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import Badge, { BadgeVariant } from 'components/Badge/Badge';
import { t } from 'locales/i18n';

interface IAssessmentTypeCardProps {
  title: string;
  icon: JSX.Element;
  bodyText: string;
  comingSoon?: boolean;
  linkUrl?: string;
  dataTestId?: string;
}

const AssessmentTypeCard = ({
  title,
  icon,
  bodyText,
  comingSoon,
  linkUrl,
  dataTestId,
}: IAssessmentTypeCardProps) => {
  const [mouseHover, setMouseHover] = useState(false);
  const navigate = useNavigate();

  const assessmentCardContainer = twMerge(
    'mx-auto',
    'block',
    'w-[24.5rem]',
    'rounded-2xl',
    'bg-white',
    'p-2',
    'py-12',
    'ring-1',
    'ring-kira-slate-200',
    'transition-all',
    'ease-in-out',
    'delay-50',
    'duration-200',
    'lg:mx-0',
    !comingSoon &&
      'hover:ring-2 hover:ring-kira-blue-800 hover:shadow-kira-blue-800 hover:drop-shadow-lg hover:-translate-y-1'
  );

  const iconClass = twMerge(
    'flex',
    'h-44',
    'items-center',
    'pb-[3.5rem]',
    !!comingSoon && 'opacity-50'
  );

  const titleClass = twMerge('prose-title-h2', !!comingSoon && 'opacity-50');

  const bodyClass = twMerge(
    'prose-body-md-reg',
    'py-5',
    'px-10',
    'text-kira-slate-700',
    !!comingSoon && 'opacity-50'
  );

  const selectButton = twMerge(
    'prose-button-lg',
    'duration-400',
    'block',
    'text-kira-blue-800',
    'transition-opacity',
    'ease-in-out',
    mouseHover ? 'visible opacity-100' : 'invisible opacity-0'
  );

  const handleClick = useCallback(() => {
    navigate(`/${linkUrl || ''}`);
  }, [linkUrl]);

  const handleMouseEnter = useCallback(() => {
    setMouseHover(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setMouseHover(false);
  }, []);

  return (
    <button
      className={assessmentCardContainer}
      type="button"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      disabled={comingSoon}
      data-testid={dataTestId}
    >
      <div className={iconClass}>{icon}</div>
      <div className={titleClass}>{title}</div>
      <div className={bodyClass}>{bodyText}</div>
      <div className="flex h-6 justify-center">
        {comingSoon ? (
          <Badge variant={BadgeVariant.YELLOW} text={t('Coming soon')} />
        ) : (
          <div className={selectButton}>
            {`${t('Select')} `}
            <ArrowRightIcon className="inline-block h-4 w-4" />
          </div>
        )}
      </div>
    </button>
  );
};

export default AssessmentTypeCard;
